import React from 'react'

import { LegalDocumentLoadReport } from '@models/legalDocument.model'

/*
 * O relatório da carga que trouxe a norma.
 *
 * Até existir esta aba ele vivia só na resposta do POST, e a tela de envio o
 * descartava depois de mostrar um "enviado com sucesso" — o aviso de que o
 * título foi deduzido, ou de que a abrangência saiu da capa, sumia com o
 * redirect. Metade deles não se reconstitui relendo o documento: eles contam o
 * que o sistema decidiu no lugar de quem enviou.
 */

const styles = {
	label: 'block text-sm text-gray',
	value: 'block font-medium break-words',
	warning:
		'border-l-4 border-yellow bg-yellow-transparent px-4 py-3 rounded-r-md',
}

function Field({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div>
			<span className={styles.label}>{label}</span>
			<span className={styles.value}>{children}</span>
		</div>
	)
}

const formatDate = (value?: string) =>
	value
		? new Date(value).toLocaleString('pt-BR', {
				dateStyle: 'short',
				timeStyle: 'short',
		  })
		: '—'

/* Centavos de dólar não cabem em duas casas: uma carga custa US$ 0,0038. */
const formatCost = (value?: number) =>
	typeof value === 'number' ? `US$ ${value.toFixed(4)}` : '—'

export default function LoadReport({
	report,
}: {
	report: LegalDocumentLoadReport | null
}) {
	if (!report) {
		return (
			<div className='mt-6 max-w-[820px]'>
				<p className='text-gray leading-relaxed'>
					Esta norma entrou antes de o relatório de carga passar a ser guardado,
					e o dela não existe para ser recuperado — os avisos viviam só na
					resposta do envio.
				</p>
				<p className='text-gray leading-relaxed mt-3'>
					Para ter o relatório desta norma, envie o documento de novo. A carga
					reconhece o arquivo repetido e não relê nada à toa; o que ela grava é
					o registro que falta.
				</p>
			</div>
		)
	}

	return (
		<div className='mt-6'>
			<div className='grid grid-cols-3 gap-6'>
				<Field label='O que o documento é'>{report.family}</Field>
				<Field label='Dispositivos que entraram'>{report.provisions}</Field>
				<Field label='Enunciados de súmula'>{report.sumulas}</Field>
				<Field label='Arquivo enviado'>{report.fileName ?? '—'}</Field>
				<Field label='Carregado em'>{formatDate(report.loadedAt)}</Field>
				<Field label='Custo de IA'>{formatCost(report.costUsd)}</Field>
			</div>

			<div className='grid grid-cols-3 gap-6 mt-6'>
				<Field label='Título declarado na carga'>{report.title ?? '—'}</Field>

				<Field label='Abrangência declarada'>
					{report.scopes?.length ? report.scopes.join(', ') : '—'}
				</Field>

				<Field label='Apelidos declarados'>
					{report.nicknames?.length ? report.nicknames.join(', ') : '—'}
				</Field>
			</div>

			{report.pending && (
				<div className='mt-6 max-w-[820px]'>
					<span className={styles.label}>Ficou pendente</span>
					<p className='mt-1'>{report.pending}</p>
				</div>
			)}

			<div className='mt-8 max-w-[820px]'>
				<span className={styles.label}>
					Avisos da carga ({report.warnings?.length ?? 0})
				</span>

				{report.warnings?.length ? (
					<div className='flex flex-col gap-3 mt-2'>
						{report.warnings.map((warning) => (
							<p key={warning} className={styles.warning}>
								{warning}
							</p>
						))}
					</div>
				) : (
					<p className='mt-1 text-gray'>
						Nenhum. O documento se apresentou por inteiro e nada precisou ser
						deduzido.
					</p>
				)}
			</div>
		</div>
	)
}
